"use strict";
// Copyright (c) Microsoft Corporation.
// Licensed under the MIT license.
Object.defineProperty(exports, "__esModule", { value: true });
exports.PublishAppPackageDriver = exports.actionName = void 0;
const tslib_1 = require("tslib");
const lib_1 = require("@feathersjs/hooks/lib");
const teamsfx_api_1 = require("@microsoft/teamsfx-api");
const adm_zip_1 = tslib_1.__importDefault(require("adm-zip"));
const fs_extra_1 = tslib_1.__importDefault(require("fs-extra"));
const lodash_1 = require("lodash");
const typedi_1 = require("typedi");
const graphClient_1 = require("../../../client/graphClient");
const constants_1 = require("../../../common/constants");
const localizeUtils_1 = require("../../../common/localizeUtils");
const common_1 = require("../../../error/common");
const common_2 = require("../../utils/common");
const addStartAndEndTelemetry_1 = require("../middleware/addStartAndEndTelemetry");
const wrapUtil_1 = require("../util/wrapUtil");
const constants_2 = require("./constants");
const telemetry_1 = require("./utils/telemetry");
const odrProvider_1 = require("../../utils/odrProvider");
const child_process_1 = require("child_process");
const util_1 = require("util");
const constants_3 = require("../../constants");
const errors_1 = require("./errors");
const results_1 = require("./results");
const featureFlags_1 = require("../../../common/featureFlags");
const accountUtils_1 = require("../../../common/accountUtils");
exports.actionName = "teamsApp/publishAppPackage";
const defaultOutputNames = {
    publishedAppId: "TEAMS_APP_PUBLISHED_APP_ID",
};
let PublishAppPackageDriver = class PublishAppPackageDriver {
    description = (0, localizeUtils_1.getLocalizedString)("driver.teamsApp.description.publishDriver");
    progressTitle = (0, localizeUtils_1.getLocalizedString)("driver.teamsApp.progressBar.publishTeamsAppStep2.2");
    async execute(args, context, outputEnvVarNames) {
        const wrapContext = new wrapUtil_1.WrapDriverContext(context, exports.actionName, exports.actionName);
        const res = await this.publish(args, wrapContext, outputEnvVarNames);
        return {
            result: res,
            summaries: wrapContext.summaries,
        };
    }
    async publish(args, context, outputEnvVarNames) {
        if (process.env[featureFlags_1.FeatureFlagName.SovereignCloudEnvironment] === accountUtils_1.SovereignCloudEnvironment.GCCH ||
            process.env[featureFlags_1.FeatureFlagName.SovereignCloudEnvironment] === accountUtils_1.SovereignCloudEnvironment.DOD) {
            context.logProvider.warning((0, localizeUtils_1.getLocalizedString)("driver.teamsApp.warning.publishUnsupportedCloud", exports.actionName));
            return (0, teamsfx_api_1.ok)(new Map());
        }
        const argsValidationResult = this.validateArgs(args);
        if (argsValidationResult.isErr()) {
            return (0, teamsfx_api_1.err)(argsValidationResult.error);
        }
        if (!outputEnvVarNames) {
            outputEnvVarNames = new Map(Object.entries(defaultOutputNames));
        }
        const appPackagePath = (0, common_2.getAbsolutePath)(args.appPackagePath, context.projectPath);
        if (!(await fs_extra_1.default.pathExists(appPackagePath))) {
            return (0, teamsfx_api_1.err)(new common_1.FileNotFoundError(exports.actionName, appPackagePath, "https://aka.ms/teamsfx-actions/teamsapp-publish"));
        }
        const archivedFile = await fs_extra_1.default.readFile(appPackagePath);
        const zipEntries = new adm_zip_1.default(archivedFile).getEntries();
        const manifestFile = zipEntries.find((x) => x.entryName === constants_2.Constants.MANIFEST_FILE);
        if (!manifestFile) {
            return (0, teamsfx_api_1.err)(new common_1.FileNotFoundError(exports.actionName, constants_2.Constants.MANIFEST_FILE, "https://aka.ms/teamsfx-actions/teamsapp-publish"));
        }
        const manifestString = manifestFile.getData().toString();
        const manifest = JSON.parse(manifestString);
        const declarativeAgents = manifest.copilotExtensions?.declarativeCopilots || manifest.copilotAgents?.declarativeAgents;
        if (declarativeAgents && declarativeAgents.length > 0) {
            const declarativeAgentFile = zipEntries.find((x) => x.entryName === declarativeAgents[0].file);
            if (declarativeAgentFile) {
                const declarativeAgentContent = declarativeAgentFile.getData().toString();
                const declarativeAgentManifest = JSON.parse(declarativeAgentContent);
                if (declarativeAgentManifest.actions) {
                    for (const action of declarativeAgentManifest.actions) {
                        const actionFile = zipEntries.find((x) => x.entryName === action.file);
                        if (actionFile) {
                            const isValid = await this.verifyLocalMCPPluginCerts(actionFile);
                            if (!isValid) {
                                const message = (0, localizeUtils_1.getLocalizedString)("driver.teamsApp.error.localMcpCertVerificationFailed");
                                return (0, teamsfx_api_1.err)(results_1.AppStudioResultFactory.UserError(errors_1.AppStudioError.ValidationFailedError.name, [
                                    message,
                                    message,
                                ]));
                            }
                        }
                    }
                }
            }
        }
        // manifest.id === externalID
        const graphTokenRes = await context.m365TokenProvider.getAccessToken({
            scopes: constants_1.GraphTeamsAppCatalogReadWriteScopes,
        });
        if (graphTokenRes.isErr()) {
            return (0, teamsfx_api_1.err)(graphTokenRes.error);
        }
        const graphClient = new graphClient_1.GraphClient(context.m365TokenProvider, context.logProvider);
        let result;
        const message = (0, localizeUtils_1.getLocalizedString)("driver.teamsApp.progressBar.publishTeamsAppStep1");
        context.addSummary(message);
        try {
            const existApp = await graphClient.getStagedApp(graphTokenRes.value, manifest.id);
            if (existApp) {
                context.addSummary((0, localizeUtils_1.getLocalizedString)("driver.teamsApp.summary.publishTeamsAppExists", manifest.id));
                let executePublishUpdate = false;
                let description = (0, localizeUtils_1.getLocalizedString)("plugins.appstudio.pubWarn", existApp.displayName, existApp.publishingState);
                if (existApp.lastModifiedDateTime) {
                    description =
                        description +
                            (0, localizeUtils_1.getLocalizedString)("plugins.appstudio.lastModified", existApp.lastModifiedDateTime?.toLocaleString());
                }
                description =
                    description + (0, localizeUtils_1.getLocalizedString)("plugins.appstudio.updatePublihsedAppConfirm");
                const confirm = (0, localizeUtils_1.getLocalizedString)("core.option.confirm");
                const res = await context.ui?.showMessage("warn", description, true, confirm);
                if (res?.isOk() && res.value === confirm)
                    executePublishUpdate = true;
                if (executePublishUpdate) {
                    const message = (0, localizeUtils_1.getLocalizedString)("driver.teamsApp.progressBar.publishTeamsAppStep2.1");
                    context.addSummary(message);
                    context.logProvider.debug(message);
                    const appId = await graphClient.publishTeamsAppUpdate(graphTokenRes.value, manifest.id, archivedFile);
                    result = new Map([[outputEnvVarNames.get("publishedAppId"), appId]]);
                    (0, lodash_1.merge)(context.telemetryProperties, {
                        [telemetry_1.TelemetryPropertyKey.updateExistingApp]: "true",
                        [telemetry_1.TelemetryPropertyKey.publishedAppId]: appId,
                    });
                }
                else {
                    return (0, teamsfx_api_1.err)(new common_1.UserCancelError(exports.actionName));
                }
            }
            else {
                context.addSummary((0, localizeUtils_1.getLocalizedString)("driver.teamsApp.summary.publishTeamsAppNotExists", manifest.id));
                const message = (0, localizeUtils_1.getLocalizedString)("driver.teamsApp.progressBar.publishTeamsAppStep2.2");
                context.addSummary(message);
                context.logProvider.debug(message);
                const appId = await graphClient.publishTeamsApp(graphTokenRes.value, manifest.id, archivedFile);
                result = new Map([[outputEnvVarNames.get("publishedAppId"), appId]]);
                (0, lodash_1.merge)(context.telemetryProperties, {
                    [telemetry_1.TelemetryPropertyKey.updateExistingApp]: "false",
                });
            }
        }
        catch (e) {
            return (0, teamsfx_api_1.err)(e);
        }
        context.logProvider.info(`Publish success!`);
        context.addSummary((0, localizeUtils_1.getLocalizedString)("driver.teamsApp.summary.publishTeamsAppSuccess", manifest.id));
        if (context.platform === teamsfx_api_1.Platform.CLI) {
            const msg = (0, localizeUtils_1.getLocalizedString)("plugins.appstudio.publishSucceedNotice.cli", manifest.name.short, constants_2.Constants.TEAMS_ADMIN_PORTAL, constants_2.Constants.TEAMS_MANAGE_APP_DOC);
            context.ui?.showMessage("info", msg, false);
        }
        return (0, teamsfx_api_1.ok)(result);
    }
    validateArgs(args) {
        const invalidParams = [];
        if (!args || !args.appPackagePath) {
            invalidParams.push("appPackagePath");
        }
        if (invalidParams.length > 0) {
            return (0, teamsfx_api_1.err)(new common_1.InvalidActionInputError(exports.actionName, invalidParams, "https://aka.ms/teamsfx-actions/teamsapp-publish"));
        }
        else {
            return (0, teamsfx_api_1.ok)(undefined);
        }
    }
    async verifyLocalMCPPluginCerts(pluginFile) {
        const pluginContent = pluginFile.getData().toString();
        const pluginManifest = JSON.parse(pluginContent);
        if (!pluginManifest.runtimes || !Array.isArray(pluginManifest.runtimes)) {
            return true;
        }
        const servers = await odrProvider_1.ODRProvider.listServers();
        let allValidCerts = true;
        const localPluginRuntimes = pluginManifest.runtimes.filter((runtime) => runtime.type === "LocalPlugin");
        for (const runtime of localPluginRuntimes) {
            const localEndpoint = runtime.spec
                ?.local_endpoint;
            if (!localEndpoint || !localEndpoint.startsWith(constants_3.LocalMcpPrefix)) {
                continue;
            }
            const mcpIdentifier = localEndpoint.substring(constants_3.LocalMcpPrefix.length);
            const serverInfo = servers.find((x) => x.identifier === mcpIdentifier);
            if (!serverInfo) {
                continue;
            }
            const valid = await this.verifyPackageFamilyCertIsValid(serverInfo.packageFamily);
            if (!valid) {
                allValidCerts = false;
                break;
            }
        }
        return allValidCerts;
    }
    async verifyPackageFamilyCertIsValid(packageName) {
        const execAsync = (0, util_1.promisify)(child_process_1.exec);
        const command = `powershell.exe -Command "& Get-AppxPackage | where { $_.PackageFamilyName -eq '${packageName}' } | select { $_.SignatureKind }"`;
        try {
            const { stdout } = await execAsync(command);
            if (!stdout) {
                return false;
            }
            if (stdout.toLowerCase().includes("developer")) {
                return false;
            }
            return true;
        }
        catch (error) {
            console.error("Unable to get cert info for package name", error);
            return false;
        }
    }
};
exports.PublishAppPackageDriver = PublishAppPackageDriver;
tslib_1.__decorate([
    (0, lib_1.hooks)([(0, addStartAndEndTelemetry_1.addStartAndEndTelemetry)(exports.actionName, exports.actionName)]),
    tslib_1.__metadata("design:type", Function),
    tslib_1.__metadata("design:paramtypes", [Object, wrapUtil_1.WrapDriverContext,
        Map]),
    tslib_1.__metadata("design:returntype", Promise)
], PublishAppPackageDriver.prototype, "publish", null);
exports.PublishAppPackageDriver = PublishAppPackageDriver = tslib_1.__decorate([
    (0, typedi_1.Service)(exports.actionName)
], PublishAppPackageDriver);
//# sourceMappingURL=publishAppPackage.js.map